import * as React from "react";

/**
 * A wrapper around React.lazy that handles "Failed to fetch dynamically imported module" errors.
 * This common error happens after a new deployment when the browser tries to load a hashed
 * JS chunk that no longer exists on the server.
 *
 * When detected, we trigger a full page reload to fetch the latest version of the application.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await factory();
    } catch (error: any) {
      const message = error?.message || String(error);

      // Check for common chunk load error messages
      if (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Importing a module script failed")
      ) {
        // eslint-disable-next-line no-console
        console.warn("Chunk load error detected, reloading page...", error);

        // Use a flag to avoid infinite reload loops if the error persists
        const RELOAD_KEY = "storizone_last_chunk_reload";
        const now = Date.now();
        const lastReload = localStorage.getItem(RELOAD_KEY);

        // Only reload if we haven't reloaded in the last 10 seconds
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
          localStorage.setItem(RELOAD_KEY, now.toString());
          window.location.reload();
          // Return a dummy promise that never resolves while reloading
          return new Promise(() => {}) as any;
        }
      }

      // If it's a different error or we already reloaded recently, rethrow
      throw error;
    }
  });
}
